import { furnitureTagsDtoSchema, type FurnitureTagsDto } from '@lib/dto/furniture-tags.dto'
import { furnitureUsageDtoSchema, type FurnitureUsageDto } from '@lib/dto/plan-impact.dto'
import { api } from '@/helpers/apiClient'
import { logRequestFailure } from '@/helpers/request-outcome'
import { pageQuery } from '@/lib/pagination'

import {
  furnitureItemDtoSchema,
  furnitureItemListDtoSchema,
  type FurnitureItemDto,
  type FurnitureItemListDto,
} from '@lib/dto/furniture-item.dto'
import type { CreateFurnitureItemInput, UpdateFurnitureItemInput } from '@lib/input/furniture-item.input'

/** Service for the tenant furniture catalog endpoints. */
class FurnitureService {
  /** A venue's furniture. Omitting the venue returns the whole organization's,
   * which only the gallery and the importer want. */
  async list(
    page?: number,
    venueId?: string | null,
    tags?: readonly string[],
    untagged?: boolean,
  ): Promise<FurnitureItemListDto> {
    try {
      const params = new URLSearchParams(pageQuery(page).replace(/^\?/, ''))
      if (venueId) params.set('venueId', venueId)
      // Repeated rather than comma-joined: a tag is free text and may contain
      // a comma, which a joined list would silently split in half.
      for (const t of tags ?? []) params.append('tag', t)
      // Its own parameter, not a reserved tag value: a venue can name a tag
      // anything, and one named "untagged" used to make its own filter return
      // every piece that did not carry it.
      if (untagged === true) params.set('untagged', 'true')
      const query = params.toString()
      return await api.get(`/furniture${query === '' ? '' : `?${query}`}`, furnitureItemListDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to list furniture', {}, error)
      throw error
    }
  }

  /** The tags already in use, for suggesting. */
  async tags(venueId?: string | null): Promise<FurnitureTagsDto> {
    try {
      const q = venueId ? `?venueId=${encodeURIComponent(venueId)}` : ''
      return await api.get(`/furniture/tags${q}`, furnitureTagsDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load furniture tags', {}, error)
      throw error
    }
  }

  async create(input: CreateFurnitureItemInput): Promise<FurnitureItemDto> {
    try {
      return await api.post('/furniture', input, furnitureItemDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to create furniture item', {}, error)
      throw error
    }
  }

  /** Where a piece is in use — plans, layouts, arrangements — before it is changed. */
  async usage(id: string): Promise<FurnitureUsageDto> {
    try {
      return await api.get(`/furniture/${id}/usage`, furnitureUsageDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to load where a furniture item is used', { id }, error)
      throw error
    }
  }

  async update(id: string, input: UpdateFurnitureItemInput): Promise<FurnitureItemDto> {
    try {
      return await api.patch(`/furniture/${id}`, input, furnitureItemDtoSchema)
    } catch (error) {
      logRequestFailure('Failed to update furniture item', {}, error)
      throw error
    }
  }
}

export const furnitureService = new FurnitureService()
